import {
  EditOutlined as EditOutlinedIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { Box, PaletteOptions, Tooltip, useTheme } from "@mui/material";
import { Icon, schemeAiPalette } from "@platform/design-system";
import { forwardRef, useState } from "react";
import {
  CELL_BROKER_HIGHLIGHT_BG,
  CellRendererParams,
  aiReportedNoValue,
  cornerTriangleSx,
  iconOverlaySx,
  isGridDefault,
  parsePlanField,
} from "@platform/plan-grid-core";
import {
  AIExtractedFieldCard,
  type AIExtractedRow,
} from "@platform/plan-grid-core";
import { useAiFieldLookup } from "@platform/plan-grid-core";
import {
  useCellFormatOverride,
  type CellFormatOverride,
} from "@platform/plan-grid-core";
import {
  useCellReviews,
  type CellReview,
  type CellReviewContextValue,
} from "@platform/plan-grid-core";
import { useCellViewRendererOverride } from "@platform/plan-grid-core";
import { CellReviewShell, FieldCommentCorner } from "@platform/plan-grid-core";

import { useSbsCellState } from "../../context/SbsCellStateContext";
import {
  CELL_DIFFERENCE_BG,
  CELL_DIFFERENCE_ICON_COLOR,
  DIFFERENCE_TOOLTIP_BG,
  DIFFERENCE_TOOLTIP_TEXT,
} from "../../../../utilities/differenceStyles";

import type { SbsStatusType } from "@platform/plan-grid-core";
import type { ComponentType, HTMLAttributes } from "react";
import type { SbsCellMeta } from "../../context/SbsCellStateContext";
import type { AiFieldData, AiFieldLookupMap } from "@platform/plan-grid-core";

type Color = Exclude<keyof NonNullable<PaletteOptions["ai"]>, "chips">;

const CORNER_SIZE = 9;

type StatusIconProps = HTMLAttributes<HTMLSpanElement> & {
  sx?: Record<string, unknown>;
};

/**
 * The flag icon from the design-system catalog. The catalog icon takes no
 * positioning or colour of its own, so a span carries both and the icon
 * follows its size and colour.
 */
const DifferenceStatusIcon = forwardRef<HTMLSpanElement, StatusIconProps>(
  ({ sx, ...rest }, ref) => (
    <Box
      ref={ref}
      component="span"
      sx={{ display: "inline-flex", "& svg": { fontSize: "inherit" }, ...sx }}
      {...rest}
    >
      <Icon name="syncProblem" />
    </Box>
  ),
);
DifferenceStatusIcon.displayName = "DifferenceStatusIcon";

const STATUS_ICONS: Partial<
  Record<
    SbsStatusType,
    {
      // An `ai` palette key, or a raw colour for an icon with no palette slot.
      color: string;
      Icon: ComponentType<{ sx?: Record<string, unknown> }>;
      tooltip?: string;
    }
  >
> = {
  extraction_error: { color: "extractionError", Icon: WarningAmberIcon },
  edited: { color: "edited", Icon: EditOutlinedIcon },
  difference: {
    color: CELL_DIFFERENCE_ICON_COLOR,
    Icon: DifferenceStatusIcon,
    tooltip: DIFFERENCE_TOOLTIP_TEXT,
  },
};

const TRANSPARENT = "transparent";

const STATUS_BG: Partial<Record<SbsStatusType, Color>> = {
  needs_review: "reviewBackground",
};

// Background-only "highlight" tints. Values are either an `ai` palette key
// (resolved through `resolveBackgroundColor`, so it follows the colour scheme)
// or a raw colour for tints that have no palette slot. Multiple highlights may
// be present in `statuses`; the first match wins for the background colour.
// The "difference" fill is not here: it yields to every tint above and to the
// `STATUS_BG` Needs Review fill, so it is applied last.
const HIGHLIGHT_BG: Partial<Record<SbsStatusType, string>> = {
  highlight_needs_review: "reviewBackground",
  highlight_broker_review: CELL_BROKER_HIGHLIGHT_BG,
};

/** Merge/split metadata attached to a DataRow for SBS rendering. */
export interface SbsMergeMeta {
  /** When true the row spans all plan columns (colSpan = total). */
  merged: boolean;
  /** Nested child rows when a benefit has been split. */
  splitRows?: unknown[];
}

/** Params shape for the SBS cell renderer. */
export interface SbsCellRendererParams extends CellRendererParams {
  data: {
    benefit?: {
      label?: string;
      networkDependent?: boolean;
      // Spec default the row-builder seeds into empty cells; used to tell a
      // seeded default apart from a real value (see isGridDefault).
      defaultValue?: unknown;
      cell?: Record<string, unknown>;
    };
    benefitKey?: string;
    meta?: Record<string, unknown>;
    sbsMerge?: SbsMergeMeta;
  };
}

// ---------------------------------------------------------------------------
// Helpers (plain functions — no hooks, safe to call outside the component)
// ---------------------------------------------------------------------------

function resolveAiField(
  params: SbsCellRendererParams,
  aiFieldLookup: AiFieldLookupMap,
): AiFieldData | undefined {
  const colField = params.colDef?.field;
  const benefitKey = params.data?.benefitKey;
  if (!colField || !benefitKey) return undefined;
  const parsed = parsePlanField(colField);
  if (!parsed) return undefined;
  const { planId, network } = parsed;
  if (network) {
    const withNetwork = `${planId}::${benefitKey}::${network}`;
    if (aiFieldLookup[withNetwork]) return aiFieldLookup[withNetwork];
  }
  return aiFieldLookup[`${planId}::${benefitKey}`];
}

function buildAiRow(
  params: SbsCellRendererParams,
  aiField: AiFieldData,
): AIExtractedRow {
  return {
    className: aiField.planName ?? "-",
    fieldName: params.data?.benefit?.label ?? aiField.btxFieldName,
    value: aiField.value === "NOT_FOUND" ? "-" : aiField.value,
    pdfPage: aiField.pageNumber != null ? `Page ${aiField.pageNumber}` : "-",
    pdfPageNumber: aiField.pageNumber ?? 0,
    justification: aiField.justification,
    documentDownloadUrl: aiField.documentDownloadUrl ?? undefined,
    documentName: aiField.documentName ?? undefined,
  };
}

function deriveFieldPath(
  colField: string | undefined,
  benefitKey: string | undefined,
): { planId: string | undefined; fieldPath: string | undefined } {
  if (!colField || !benefitKey)
    return { planId: undefined, fieldPath: undefined };
  const [planId, ...networkParts] = colField.split(".");
  const network = networkParts.length > 0 ? networkParts.join(".") : undefined;
  return {
    planId,
    fieldPath: network ? `${benefitKey}::${network}` : benefitKey,
  };
}

function resolveCellText(
  params: SbsCellRendererParams,
  override: CellFormatOverride<SbsCellRendererParams> | null,
  fallback: string,
  valueState: SbsCellMeta["valueState"],
  hasEdited: boolean,
  hasRealValue: boolean,
): string {
  // The AI reported no value for this cell — show the authoritative "No Data
  // Found" ([ ]) indicator. The value present is usually not a real extraction
  // (the AI found nothing): an empty cell, a grid-seeded spec default, or an
  // ingestion-coerced enum. The only value worth showing is one the user
  // supplied. A manual edit wins (hasEdited), but that flag is only set for
  // edits made in this grid; an edit made in the PlanEditor lands as a real,
  // non-default value (hasRealValue) without setting it — so a genuine value
  // that isn't a seeded default also wins (CD-1504).
  if (valueState === "null" && !hasEdited && !hasRealValue) return fallback;

  const overridden = override?.(params);
  if (overridden !== undefined) return overridden;
  if (params.valueFormatted !== undefined && params.valueFormatted !== null) {
    return params.valueFormatted;
  }
  if (params.value === undefined || params.value === null) return fallback;
  return String(params.value);
}

function resolveCellBackground(
  hasReview: boolean,
  hasBrokerHighlight: boolean,
  statuses: SbsStatusType[],
): string {
  if (hasReview) {
    return HIGHLIGHT_BG["highlight_needs_review"] ?? TRANSPARENT;
  }
  if (hasBrokerHighlight) {
    return HIGHLIGHT_BG["highlight_broker_review"] ?? TRANSPARENT;
  }
  const highlight = statuses.find((f) => f in HIGHLIGHT_BG);
  if (highlight) {
    return HIGHLIGHT_BG[highlight] ?? TRANSPARENT;
  }
  const paletteBg = statuses.find((f) => f in STATUS_BG);
  if (paletteBg) {
    const colorKey = STATUS_BG[paletteBg] ?? TRANSPARENT;
    return colorKey;
  }
  return statuses.includes("difference") ? CELL_DIFFERENCE_BG : TRANSPARENT;
}

/** Resolve the persisted cell-review flags for a cell (review/highlight/edited). */
function resolveReviewState(
  planId: string | undefined,
  fieldPath: string | undefined,
  reviews: CellReviewContextValue,
): {
  review: CellReview | undefined;
  hasBrokerHighlight: boolean;
  hasEdited: boolean;
} {
  if (!planId || !fieldPath) {
    return { review: undefined, hasBrokerHighlight: false, hasEdited: false };
  }
  return {
    review: reviews.getReview(planId, fieldPath),
    hasBrokerHighlight: reviews.getBrokerHighlight(planId, fieldPath),
    hasEdited: reviews.getEdited(planId, fieldPath),
  };
}

/**
 * Mapping error: the AI extracted a real value but the cell can't show it. A
 * not-found value (empty/NOT_FOUND) is not an error — it renders as "No Data
 * Found" with no warning icon — and an in-grid edit resolves the cell, so it
 * never warns. Two ways a real extraction fails to land:
 *   - nothing real in the cell (`hasRealValue` is false when the cell is empty
 *     OR holds only a grid-seeded default, so a seeded default doesn't mask a
 *     mapping error), or
 *   - the cell holds a value the grid can't represent, so it renders as the
 *     "-" fallback anyway (e.g. an enum outside the field's option list or a
 *     disabled dependent cell).
 */
function deriveMappingError(
  aiField: AiFieldData | undefined,
  hasRealValue: boolean,
  // The cell text as resolved BEFORE the mapping-error substitution — passing
  // the substituted text would be circular (it is always "-" on an error).
  baseText: string,
  hasEdited: boolean,
): boolean {
  if (
    aiField === undefined ||
    aiField.indicatorOnly ||
    aiReportedNoValue(aiField.value) ||
    hasEdited
  ) {
    return false;
  }
  return !hasRealValue || baseText === "-";
}

/**
 * Builds the list of status icons to render. The "Edited" pencil is persisted
 * as a cell review rather than an SBS status, and mapping errors are derived
 * from the cell value at render time, so both are folded in here.
 */
function buildIconStatuses(
  statuses: SbsStatusType[],
  hasEdited: boolean,
  hasMappingError: boolean,
): SbsStatusType[] {
  const withMappingError =
    hasMappingError && !statuses.includes("extraction_error")
      ? [...statuses, "extraction_error" as const]
      : statuses;
  // An edited cell keeps its difference fill, but the pencil replaces the
  // difference icon: the analyst has since changed the value.
  const iconStatuses = withMappingError.filter(
    (f) => f in STATUS_ICONS && !(hasEdited && f === "difference"),
  );
  return hasEdited && !iconStatuses.includes("edited")
    ? [...iconStatuses, "edited"]
    : iconStatuses;
}

function renderCellContent(
  params: SbsCellRendererParams,
  text: string,
  cellViewRendererOverride: ReturnType<typeof useCellViewRendererOverride>,
  fallbackNode: React.ReactNode,
): React.ReactNode {
  const overridden = cellViewRendererOverride?.(params, text, fallbackNode);
  if (overridden !== undefined) return overridden;
  return fallbackNode ?? <Box component="span">{text}</Box>;
}

// ---------------------------------------------------------------------------
// AiTooltip — extracted to keep SbsCellRenderer under the complexity limit
// ---------------------------------------------------------------------------

const AiTooltip = ({
  params,
  aiField,
}: {
  params: SbsCellRendererParams;
  aiField: AiFieldData;
}): React.JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isOverTrigger, setIsOverTrigger] = useState(false);
  const [isOverCard, setIsOverCard] = useState(false);

  return (
    <>
      <Box
        data-testid="sbs-cell-ai-tooltip-trigger"
        onMouseEnter={(e) => {
          setAnchorEl(e.currentTarget);
          setIsOverTrigger(true);
        }}
        onMouseLeave={() => {
          setIsOverTrigger(false);
        }}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: CORNER_SIZE,
          height: CORNER_SIZE,
          cursor: "pointer",
          zIndex: 2,
        }}
      />
      {anchorEl !== null && (
        <AIExtractedFieldCard
          row={buildAiRow(params, aiField)}
          anchorEl={anchorEl}
          open={isOverTrigger || isOverCard}
          onMouseEnter={() => setIsOverCard(true)}
          onMouseLeave={() => setIsOverCard(false)}
        />
      )}
    </>
  );
};

/**
 * Renders the blue corner triangle and (when AI data is available) the hover tooltip.
 * The corner records that the value originated with the AI, which an in-grid edit does
 * not undo, so it stays put and the "Edited" pencil is shown next to it.
 */
const AiExtractedIndicators = ({
  params,
  aiField,
  extractedColor,
}: {
  params: SbsCellRendererParams;
  aiField: AiFieldData | undefined;
  extractedColor: string | undefined;
}): React.JSX.Element => (
  <>
    <Box
      data-testid="sbs-cell-ai-extracted"
      sx={cornerTriangleSx("top-left", extractedColor ?? "")}
    />
    {aiField && <AiTooltip params={params} aiField={aiField} />}
  </>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function resolveBackgroundColor(
  rawBg: string,
  aiPalette: NonNullable<PaletteOptions["ai"]>,
): string {
  return rawBg in aiPalette ? (aiPalette[rawBg as Color] as string) : rawBg;
}

type StatusIconSpec = {
  status: SbsStatusType;
  spec: NonNullable<(typeof STATUS_ICONS)[SbsStatusType]>;
};

/**
 * Drops statuses that have no icon, so the rendered icons occupy consecutive
 * slots — the same count the cell reserves text gutter for.
 */
function resolveStatusIcons(statuses: SbsStatusType[]): StatusIconSpec[] {
  return statuses.flatMap((status) => {
    const spec = STATUS_ICONS[status];
    return spec ? [{ status, spec }] : [];
  });
}

function renderStatusIcons(
  icons: StatusIconSpec[],
  aiPalette: NonNullable<PaletteOptions["ai"]>,
): React.ReactNode[] {
  return icons.map(({ status, spec }, i) => {
    const StatusIcon = spec.Icon;
    const icon = (
      <StatusIcon
        // eslint-disable-next-line react/no-array-index-key
        key={`${status}-${i}`}
        data-testid={`sbs-cell-status-${status}`}
        sx={{
          ...iconOverlaySx("left", { index: i }),
          color: resolveBackgroundColor(spec.color, aiPalette),
        }}
      />
    );

    return spec.tooltip ? (
      <Tooltip
        // eslint-disable-next-line react/no-array-index-key
        key={`${status}-${i}`}
        title={spec.tooltip}
        slotProps={{
          tooltip: { sx: { backgroundColor: DIFFERENCE_TOOLTIP_BG } },
        }}
      >
        {icon}
      </Tooltip>
    ) : (
      icon
    );
  });
}

/** A cell's metadata, with a network column's own difference flag. */
const lookupCellMeta = (
  sbsState: ReturnType<typeof useSbsCellState> | null | undefined,
  benefitKey: string,
  cellStateKey: string,
  columnField?: string,
): SbsCellMeta | undefined =>
  sbsState?.getCellMeta(
    benefitKey,
    cellStateKey,
    parsePlanField(columnField)?.network,
  );

export const SbsCellRenderer: ComponentType<SbsCellRendererParams> = (
  params: SbsCellRendererParams,
): React.JSX.Element => {
  const theme = useTheme();
  const aiPalette = schemeAiPalette(theme) as NonNullable<PaletteOptions["ai"]>;
  const sbsState = useSbsCellState();
  const cellFormatOverride = useCellFormatOverride<SbsCellRendererParams>();
  const cellViewRendererOverride = useCellViewRendererOverride();
  const cellReviews = useCellReviews();
  const aiFieldLookup = useAiFieldLookup();

  const { planId, fieldPath } = deriveFieldPath(
    params.colDef?.field,
    params.data?.benefitKey,
  );
  const { review, hasBrokerHighlight, hasEdited } = resolveReviewState(
    planId,
    fieldPath,
    cellReviews,
  );

  const benefitKey = params.data?.benefitKey ?? "";
  const cellStateKey = planId ?? params.colDef?.field ?? "";
  const cellMeta = lookupCellMeta(
    sbsState,
    benefitKey,
    cellStateKey,
    params.colDef?.field,
  );
  const statuses = cellMeta?.statuses ?? [];
  const hasAiExtracted = statuses.includes("ai_extracted");
  const aiField = hasAiExtracted
    ? resolveAiField(params, aiFieldLookup)
    : undefined;

  // A cell "has a real value" only if its value isn't empty and isn't a
  // grid-seeded default (getValueWithDefault injects the field's spec default
  // into empty cells). Treating a seeded default as "no real value" keeps
  // not-found cells showing "No Data Found" instead of the default (CD-1504).
  const hasRealValue = !isGridDefault(params.value, params.data?.benefit);
  const baseText = resolveCellText(
    params,
    cellFormatOverride,
    cellMeta?.valueState === "null" ? "[ ]" : "-",
    cellMeta?.valueState,
    hasEdited,
    hasRealValue,
  );
  const hasMappingError = deriveMappingError(
    aiField,
    hasRealValue,
    baseText,
    hasEdited,
  );
  // A mapping-error cell never shows its underlying value (a grid-seeded
  // default or an unrepresentable enum would read as real quote data): the
  // warning icon renders next to a plain "-" instead.
  const text = hasMappingError ? "-" : baseText;

  const rawBg = resolveCellBackground(
    Boolean(review),
    hasBrokerHighlight,
    statuses,
  );
  const backgroundColor = resolveBackgroundColor(rawBg, aiPalette);
  // The "Edited" pencil is persisted as a cell review (not in the SBS status
  // map), so fold it into the rendered icons when present.
  const renderedIconStatuses = buildIconStatuses(
    statuses,
    hasEdited,
    hasMappingError,
  );
  const statusIcons = resolveStatusIcons(renderedIconStatuses);

  return (
    <CellReviewShell
      className="sbs-cell"
      backgroundColor={backgroundColor}
      iconCount={statusIcons.length}
      overlays={
        <>
          {renderStatusIcons(statusIcons, aiPalette)}

          {review?.comment && <FieldCommentCorner comment={review.comment} />}

          {hasAiExtracted && (
            <AiExtractedIndicators
              params={params}
              aiField={aiField}
              extractedColor={aiPalette.extracted}
            />
          )}
        </>
      }
    >
      {renderCellContent(
        params,
        text,
        cellViewRendererOverride,
        params.children,
      )}
    </CellReviewShell>
  );
};

SbsCellRenderer.displayName = "SbsCellRenderer";
